import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { DatePipe } from '@angular/common';
import { EhrService } from '@beautyapp/core';

@Component({
  selector: 'beautyapp-ehr-document-upload',
  imports: [
    FormsModule,
    NzUploadModule,
    NzButtonModule,
    NzIconModule,
    NzSelectModule,
    NzTableModule,
    NzSpinModule,
    NzTagModule,
    DatePipe,
  ],
  template: `
    <div class="space-y-4">
      <!-- Upload section -->
      <div class="border border-dashed border-gray-300 rounded-lg p-4">
        <nz-upload
          [nzAction]="''"
          [nzBeforeUpload]="beforeUpload"
          [nzShowUploadList]="false"
          [nzAccept]="'.pdf,.png,.jpg,.jpeg,.doc,.docx'"
        >
          <button nz-button>
            <span nz-icon nzType="upload"></span>
            ფაილის არჩევა
          </button>
        </nz-upload>

        @if (selectedFile()) {
          <div class="mt-3 flex items-center gap-3">
            <span nz-icon nzType="file" nzTheme="outline" class="text-blue-500"></span>
            <span class="text-sm text-gray-700">{{ selectedFile()!.name }}</span>
          </div>
        }
      </div>

      <!-- Document type selector -->
      <div>
        <label class="block text-sm text-gray-700 mb-1">დოკუმენტის ტიპი</label>
        <nz-select [(ngModel)]="documentType" class="w-full" nzPlaceHolder="აირჩიეთ ტიპი">
          <nz-option nzValue="INFORMED_CONSENT" nzLabel="ინფორმირებული თანხმობა"></nz-option>
          <nz-option nzValue="PRESCRIPTION" nzLabel="დანიშნულება"></nz-option>
          <nz-option nzValue="OTHER" nzLabel="სხვა"></nz-option>
        </nz-select>
      </div>

      <!-- Upload button -->
      <button
        nz-button
        nzType="primary"
        [nzLoading]="uploading()"
        [disabled]="!selectedFile()"
        (click)="upload()"
        class="w-full"
      >
        ატვირთვა
      </button>

      <!-- Existing documents list -->
      @if (loading()) {
        <nz-spin nzSimple></nz-spin>
      } @else if (documents().length) {
        <div class="mt-4">
          <h4 class="text-sm font-semibold text-gray-700 mb-2">ატვირთული დოკუმენტები</h4>
          <nz-table
            [nzData]="documents()"
            nzSize="small"
            [nzShowPagination]="false"
            nzTemplateMode
          >
            <thead>
              <tr>
                <th>ფაილის სახელი</th>
                <th>ტიპი</th>
                <th>თარიღი</th>
                <th nzWidth="60px"></th>
              </tr>
            </thead>
            <tbody>
              @for (doc of documents(); track doc.id) {
                <tr>
                  <td>{{ doc.fileName }}</td>
                  <td>
                    <nz-tag [nzColor]="getTypeColor(doc.documentType)">
                      {{ getTypeLabel(doc.documentType) }}
                    </nz-tag>
                  </td>
                  <td>{{ doc.createdAt | date:'dd/MM/yyyy HH:mm' }}</td>
                  <td>
                    <a [href]="doc.fileUrl" target="_blank">
                      <span nz-icon nzType="download" nzTheme="outline"></span>
                    </a>
                  </td>
                </tr>
              }
            </tbody>
          </nz-table>
        </div>
      }
    </div>
  `,
})
export class EhrDocumentUploadComponent implements OnInit {
  private data = inject(NZ_MODAL_DATA);
  private modal = inject(NzModalRef);
  private ehrService = inject(EhrService);
  private messageService = inject(NzMessageService);

  selectedFile = signal<File | null>(null);
  documentType = 'OTHER';
  uploading = signal(false);
  loading = signal(false);
  documents = signal<any[]>([]);

  ngOnInit() {
    this.loadDocuments();
  }

  beforeUpload = (file: NzUploadFile): boolean => {
    this.selectedFile.set(file.originFileObj as File);
    return false; // Prevent auto-upload
  };

  upload() {
    const file = this.selectedFile();
    const ehrId = this.data.ehrId;
    if (!file || !ehrId) return;

    this.uploading.set(true);
    this.ehrService.uploadDocument(ehrId, file, this.documentType).subscribe({
      next: () => {
        this.uploading.set(false);
        this.selectedFile.set(null);
        this.messageService.success('დოკუმენტი წარმატებით აიტვირთა');
        this.loadDocuments();
      },
      error: () => {
        this.uploading.set(false);
        this.messageService.error('ატვირთვა ვერ მოხერხდა');
      },
    });
  }

  loadDocuments() {
    const ehrId = this.data.ehrId;
    if (!ehrId) return;

    this.loading.set(true);
    this.ehrService.getDocuments(ehrId).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        this.documents.set(res?.documents || []);
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  getTypeColor(type: string): string {
    switch (type) {
      case 'INFORMED_CONSENT': return 'blue';
      case 'PRESCRIPTION': return 'green';
      default: return 'default';
    }
  }

  getTypeLabel(type: string): string {
    switch (type) {
      case 'INFORMED_CONSENT': return 'თანხმობა';
      case 'PRESCRIPTION': return 'დანიშნულება';
      default: return 'სხვა';
    }
  }
}
